import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import axios from 'axios';
import usePermissions from './usePermissions';
import {
 DollarSign, TrendingUp, TrendingDown, Calendar, Search, Filter,
 Plus, Edit, Trash2, Save, X, FileText, AlertCircle, BarChart3,
 Wallet, CreditCard, Building, Tag, ChevronDown, ChevronUp,
 Download, Printer, RefreshCw, Eye, Clock, Hash, FileSpreadsheet,
 Settings, MoreHorizontal, ArrowDown, User
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const createAdvancedExcelFileXLSX = (data, summary, options, getCategoryName, getPaymentMethodName) => {
  if (typeof window.XLSX === 'undefined') {
    throw new Error('مكتبة Excel غير متوفرة');
  }
  
  const XLSX = window.XLSX;
  
  // إنشاء workbook جديد
  const workbook = XLSX.utils.book_new();
  
  // إعداد البيانات للورقة
  const wsData = [];
  
  // تاريخ التقرير
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });
  
  // إضافة العنوان الرئيسي
  wsData.push(['', '', '', `تقرير معاملات الشركة المالي - ${formattedDate}`, '', '', '', '']);
  wsData.push([]);
  
  // إضافة الملخص المالي
  wsData.push(['', '', 'الملخص المالي', '', '', '', '', '']);
  wsData.push(['', '', `إجمالي الإيرادات: ${summary.totalIncome.toLocaleString()} جنيه`, '', '', '', '', '']);
  wsData.push(['', '', `إجمالي المصروفات: ${summary.totalExpenses.toLocaleString()} جنيه`, '', '', '', '', '']);
  wsData.push(['', '', `صافي الرصيد: ${summary.balance.toLocaleString()} جنيه`, '', '', '', '', '']);
  wsData.push([]);
  
  // رؤوس الأعمدة
  wsData.push([
    'م',
    'التاريخ', 
    'نوع المعاملة',
    'التصنيف',
    'الوصف',
    'الملاحظات',
    'المبلغ (جنيه)',
    'طريقة الدفع'
  ]);
  
  // إضافة بيانات المعاملات
  data.forEach((transaction, index) => {
    wsData.push([
      index + 1,
      formatDateForExcel(transaction.date),
      transaction.type === 'income' ? 'إيراد' : 'مصروف',
      getCategoryName(transaction.categoryId),
      transaction.description || '',
      transaction.notes || '',
      parseFloat(transaction.amount),
      getPaymentMethodName(transaction.paymentMethodId)
    ]);
  });
  
  // إنشاء الورقة
  const worksheet = XLSX.utils.aoa_to_sheet(wsData);
  
  // تطبيق التنسيق المحسن
  applyAdvancedFormattingXLSX(worksheet, data.length, wsData);
  
  // إضافة الورقة للـ workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'تقرير المعاملات');
  
  return workbook;
};

const applyAdvancedFormattingXLSX = (ws, dataLength, wsData) => {
  if (typeof window.XLSX === 'undefined') {
    return;
  }
  
  const XLSX = window.XLSX;
  const range = XLSX.utils.decode_range(ws['!ref']);
  
  // تعيين عرض الأعمدة
  ws['!cols'] = [
    { wch: 8 },   // م
    { wch: 18 },  // التاريخ
    { wch: 16 },  // نوع المعاملة
    { wch: 20 },  // التصنيف
    { wch: 30 },  // الوصف
    { wch: 35 },  // الملاحظات
    { wch: 18 },  // المبلغ
    { wch: 20 }   // طريقة الدفع
  ];
  
  // تعيين ارتفاع الصفوف
  if (!ws['!rows']) ws['!rows'] = [];
  for (let i = 0; i <= range.e.r; i++) {
    if (!ws['!rows'][i]) ws['!rows'][i] = {};
    
    if (i === 0) {
      ws['!rows'][i].hpt = 40; // العنوان الرئيسي
    } else if (i >= 2 && i <= 6) {
      ws['!rows'][i].hpt = 30; // منطقة الملخص
    } else if (i === 7) {
      ws['!rows'][i].hpt = 35; // رؤوس الأعمدة
    } else {
      ws['!rows'][i].hpt = 25; // باقي الصفوف
    }
  }
  
  // تطبيق التنسيق على الخلايا
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
      if (!ws[cellRef]) continue;
      
      if (!ws[cellRef].s) ws[cellRef].s = {};
      
      // تنسيق العنوان الرئيسي (الصف الأول)
      if (R === 0) {
        ws[cellRef].s = {
          font: { name: 'Calibri', sz: 18, bold: true, color: { rgb: '000080' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          fill: { fgColor: { rgb: 'E6F3FF' } },
          border: {
            top: { style: 'medium', color: { rgb: '000080' } },
            bottom: { style: 'medium', color: { rgb: '000080' } },
            left: { style: 'medium', color: { rgb: '000080' } },
            right: { style: 'medium', color: { rgb: '000080' } }
          }
        };
      }
      // تنسيق منطقة الملخص (الصفوف 2-6)
      else if (R >= 2 && R <= 6) {
        ws[cellRef].s = {
          font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: '000000' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          fill: { fgColor: { rgb: 'F0F8FF' } },
          border: {
            top: { style: 'thin', color: { rgb: '666666' } },
            bottom: { style: 'thin', color: { rgb: '666666' } },
            left: { style: 'thin', color: { rgb: '666666' } },
            right: { style: 'thin', color: { rgb: '666666' } }
          }
        };
      }
      // تنسيق رؤوس الأعمدة (الصف 7)
      else if (R === 7) {
        ws[cellRef].s = {
          font: { name: 'Calibri', sz: 12, bold: true, color: { rgb: 'FFFFFF' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          fill: { fgColor: { rgb: '4472C4' } },
          border: {
            top: { style: 'medium', color: { rgb: '000000' } },
            bottom: { style: 'medium', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          }
        };
      }
      // تنسيق بيانات المعاملات (الصفوف 8+)
      else if (R > 7) {
        const isEvenRow = (R - 8) % 2 === 0;
        const rowData = ws[XLSX.utils.encode_cell({ c: 2, r: R })];
        
        ws[cellRef].s = {
          font: { name: 'Calibri', sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: {
            top: { style: 'thin', color: { rgb: 'CCCCCC' } },
            bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
            right: { style: 'thin', color: { rgb: 'CCCCCC' } }
          }
        };
        
        // لون الخلفية للصفوف المتناوبة
        if (isEvenRow) {
          ws[cellRef].s.fill = { fgColor: { rgb: 'F8F9FA' } };
        } else {
          ws[cellRef].s.fill = { fgColor: { rgb: 'FFFFFF' } };
        }
        
        // تنسيق خاص لعمود نوع المعاملة
        if (C === 2) {
          if (rowData && rowData.v === 'إيراد') {
            ws[cellRef].s.font.color = { rgb: '008000' };
            ws[cellRef].s.font.bold = true;
          } else if (rowData && rowData.v === 'مصروف') {
            ws[cellRef].s.font.color = { rgb: 'FF0000' };
            ws[cellRef].s.font.bold = true;
          }
        }
        
        // تنسيق خاص لعمود المبلغ
        if (C === 6) {
          ws[cellRef].s.font.bold = true;
          ws[cellRef].s.numFmt = '#,##0';
          if (rowData && rowData.v === 'إيراد') {
            ws[cellRef].s.font.color = { rgb: '008000' };
          } else if (rowData && rowData.v === 'مصروف') {
            ws[cellRef].s.font.color = { rgb: 'FF0000' };
          }
        }
        
        // تنسيق عمود الترقيم
        if (C === 0) {
          ws[cellRef].s.font.bold = true;
          ws[cellRef].s.fill = { fgColor: { rgb: 'E9ECEF' } };
        }
      }
    }
  }
  
  // دمج خلايا العنوان الرئيسي
  if (!ws['!merges']) ws['!merges'] = [];
  
  ws['!merges'].push({
    s: { c: 3, r: 0 },
    e: { c: 7, r: 0 }
  });
  
  // دمج خلايا الملخص
  for (let i = 2; i <= 6; i++) {
    ws['!merges'].push({
      s: { c: 2, r: i },
      e: { c: 7, r: i }
    });
  }
};

const formatDateForExcel = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
};

const formatDateTimeForExcel = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const loadSheetJSLibrary = () => {
  return new Promise((resolve, reject) => {
    if (typeof window.XLSX !== 'undefined') {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.onload = () => {
      if (typeof window.XLSX !== 'undefined' && window.XLSX.utils) {
        resolve();
      } else {
        reject(new Error('فشل في تحميل مكتبة Excel'));
      }
    };
    script.onerror = () => reject(new Error('فشل في تحميل مكتبة Excel'));
    document.head.appendChild(script);
  });
};

const CompanyTransactions = () => {
 const navigate = useNavigate();
 const { hasPermission } = usePermissions();
 const canDo = (permission) => hasPermission('إيرادات ومصروفات - الصلاحيات', permission);

 const getCurrentUser = () => {
     try {
         const user = JSON.parse(localStorage.getItem('user'));
         return user?.Username || 'System';
     } catch { return 'System'; }
 };
 
 // الحالات الرئيسية
 const [transactions, setTransactions] = useState([]);
 
const [virtualPagination, setVirtualPagination] = useState({
  currentPage: 1,
  itemsPerPage: 30
});

// أضف هذا السطر مع باقي useState في أول الكومبوننت
const [itemsPerPageOptions] = useState([30, 50, 100]);


 const [categories, setCategories] = useState([]);
 const [paymentMethods, setPaymentMethods] = useState([]);
 const [summary, setSummary] = useState({
   totalIncome: 0,
   totalExpenses: 0,
   balance: 0
 });
 
 // حالات pagination
 const [pagination, setPagination] = useState({
   currentPage: 1,
   totalPages: 1,
   totalCount: 0,
   hasMore: false,
   limit: 30
 });
 const [loadingMore, setLoadingMore] = useState(false);
 
 // حالات الفلترة والبحث
 const [search, setSearch] = useState('');
 const [filterType, setFilterType] = useState('all');
 const [filterCategory, setFilterCategory] = useState('all');
 const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
 const [dateRange, setDateRange] = useState({
   start: '',
   end: ''
 });
 
 // حالات النماذج
 const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
 const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
 const [isExportModalOpen, setIsExportModalOpen] = useState(false);
 const [editingTransaction, setEditingTransaction] = useState(null);
 const [editingCategory, setEditingCategory] = useState(null);
 
 // حالات التحميل والأخطاء
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 
// في الحالة الافتراضية لـ exportOptions، عدّل إلى:
const [exportOptions, setExportOptions] = useState({
  format: 'excel',
  includeFilters: true,
  includeSummary: true,
  dateRange: 'current', // تغيير القيمة الافتراضية
  customStartDate: '',
  customEndDate: '',
  selectedCategories: [],
  selectedTypes: ['income', 'expense']
});

 // نموذج المعاملة
 const [transactionForm, setTransactionForm] = useState({
   type: 'income',
   amount: '',
   categoryId: '',
   paymentMethodId: '',
   paymentMethodType: '',
   description: '',
   notes: '',
   date: new Date().toISOString().split('T')[0]
 });
 
 // نموذج التصنيف
 const [categoryForm, setCategoryForm] = useState({
   name: '',
   type: 'expense',
   description: ''
 });
 
 // عرض تفاصيل المعاملة
 const [viewingTransaction, setViewingTransaction] = useState(null);
 const [categorySearch, setCategorySearch] = useState('');
const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
// التحميل الأولي للبيانات الثابتة فقط، بدون تحميل كل المعاملات مرة واحدة
useEffect(() => {
  const initializeData = async () => {
    try {
      setError(null);
      await Promise.all([
        fetchCategories(),
        fetchPaymentMethods(),
        fetchSummary()
      ]);
    } catch (err) {
      console.error('خطأ في جلب البيانات الثابتة:', err);
      setError('حدث خطأ أثناء جلب البيانات');
    }
  };
  
  initializeData();
}, []);

// تحميل المعاملات من السيرفر حسب الصفحة والفلاتر، مع تأخير بسيط أثناء الكتابة في البحث
useEffect(() => {
  const delay = search.trim() ? 350 : 0;
  const timeoutId = setTimeout(() => {
    fetchTransactions(virtualPagination.currentPage, false);
  }, delay);

  return () => clearTimeout(timeoutId);
}, [
  search,
  filterType,
  filterCategory,
  filterPaymentMethod,
  dateRange.start,
  dateRange.end,
  virtualPagination.currentPage,
  virtualPagination.itemsPerPage
]);
 
 useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.category-dropdown')) {
      setShowCategoryDropdown(false);
    }
  };

  if (showCategoryDropdown) {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }
}, [showCategoryDropdown]);

 // جلب جميع البيانات
 const fetchData = async (page = virtualPagination.currentPage) => {
   try {
     setLoading(true);
     setError(null);
     
     await Promise.all([
       fetchTransactions(page),
       fetchCategories(),
       fetchPaymentMethods(),
       fetchSummary()
     ]);
     
     setLoading(false);
   } catch (err) {
     console.error('خطأ في جلب البيانات:', err);
     setError('حدث خطأ أثناء جلب البيانات');
     setLoading(false);
   }
 };
 


const resetServerPage = () => {
  setVirtualPagination(prev => (
    prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }
  ));
};

const handleItemsPerPageChange = (newItemsPerPage) => {
  setVirtualPagination({
    currentPage: 1,
    itemsPerPage: newItemsPerPage
  });
};

const buildTransactionQueryParams = ({
  page = virtualPagination.currentPage,
  limit = virtualPagination.itemsPerPage,
  includePagination = true,
  useCurrentFilters = true,
  customFilters = {}
} = {}) => {
  const params = new URLSearchParams();

  if (includePagination) {
    params.set('page', page);
    params.set('limit', limit);
  }

  const effectiveSearch = customFilters.search ?? (useCurrentFilters ? search : '');
  const effectiveType = customFilters.type ?? (useCurrentFilters ? filterType : 'all');
  const effectiveCategory = customFilters.categoryId ?? (useCurrentFilters ? filterCategory : 'all');
  const effectivePaymentMethod = customFilters.paymentMethodId ?? (useCurrentFilters ? filterPaymentMethod : 'all');
  const effectiveStartDate = customFilters.startDate ?? (useCurrentFilters ? dateRange.start : '');
  const effectiveEndDate = customFilters.endDate ?? (useCurrentFilters ? dateRange.end : '');

  if (effectiveSearch && String(effectiveSearch).trim()) {
    params.set('search', String(effectiveSearch).trim());
  }
  if (effectiveType && effectiveType !== 'all') {
    params.set('type', effectiveType);
  }
  if (effectiveCategory && effectiveCategory !== 'all') {
    params.set('categoryId', effectiveCategory);
  }
  if (customFilters.categoryIds && customFilters.categoryIds.length > 0) {
    params.set('categoryIds', customFilters.categoryIds.join(','));
  }
  if (effectivePaymentMethod && effectivePaymentMethod !== 'all') {
    params.set('paymentMethodId', effectivePaymentMethod);
  }
  if (effectiveStartDate) {
    params.set('startDate', effectiveStartDate);
  }
  if (effectiveEndDate) {
    params.set('endDate', effectiveEndDate);
  }

  return params;
};

 // جلب المعاملات مع pagination وفلاتر من السيرفر
 const fetchTransactions = async (page = 1, append = false) => {
   try {
     setLoading(true);
     setError(null);

     const params = buildTransactionQueryParams({ page, limit: virtualPagination.itemsPerPage });
     const response = await axios.get(`${config.API_BASE_URL}/api/company-transactions?${params.toString()}`);
     
     if (append) {
       setTransactions(prev => [...prev, ...response.data.transactions]);
     } else {
       setTransactions(response.data.transactions);
     }
     
     setPagination(response.data.pagination);
   } catch (err) {
     console.error('خطأ في جلب المعاملات:', err);
     setError('حدث خطأ أثناء جلب المعاملات');
     throw err;
   } finally {
     setLoading(false);
   }
 };
 
 // جلب معاملات للتصدير عند الطلب فقط، بدون تحميلها عند فتح الصفحة
 const fetchAllTransactionsForExport = async ({ useCurrentFilters = false, customFilters = {} } = {}) => {
   try {
     const params = buildTransactionQueryParams({
       includePagination: false,
       useCurrentFilters,
       customFilters
     });
     const queryString = params.toString();
     const url = `${config.API_BASE_URL}/api/company-transactions/all-with-details${queryString ? `?${queryString}` : ''}`;
     const response = await axios.get(url);

     return response.data;
   } catch (err) {
     console.error('خطأ في جلب معاملات التصدير:', err);
     throw err;
   }
 };
 
 // تحميل المزيد من المعاملات
 const loadMoreTransactions = async () => {
   if (!pagination.hasMore || loadingMore) return;
   
   try {
     setLoadingMore(true);
     await fetchTransactions(pagination.currentPage + 1, true);
   } catch (err) {
     console.error('خطأ في تحميل المزيد:', err);
     toast.error('حدث خطأ في تحميل المزيد من البيانات');
   } finally {
     setLoadingMore(false);
   }
 };
 
 // جلب التصنيفات
 const fetchCategories = async () => {
   try {
     const response = await axios.get(`${config.API_BASE_URL}/api/transaction-categories`);
     setCategories(response.data);
   } catch (err) {
     console.error('خطأ في جلب التصنيفات:', err);
     throw err;
   }
 };
 
 // جلب طرق الدفع
 const fetchPaymentMethods = async () => {
   try {
     const response = await axios.get(`${config.API_BASE_URL}/api/banks/payment-methods`);
     if (response.data.paymentMethods) {
       const bankAccountsOnly = response.data.paymentMethods.filter(method => 
         method.type === 'bank'
       );
       setPaymentMethods(bankAccountsOnly);
     }
   } catch (err) {
     console.error('خطأ في جلب طرق الدفع:', err);
     throw err;
   }
 };
 
 // جلب الملخص المالي
 const fetchSummary = async () => {
   try {
     const response = await axios.get(`${config.API_BASE_URL}/api/company-transactions/summary`);
     setSummary(response.data);
   } catch (err) {
     console.error('خطأ في جلب الملخص:', err);
     throw err;
   }
 };

// تجهيز بيانات التصدير حسب الاختيار، مع تحميل البيانات الكبيرة عند الطلب فقط
const getExportData = async () => {
  let dataToExport = [];
  
  if (exportOptions.dateRange === 'current') {
    dataToExport = [...paginatedTransactions];
  } else if (exportOptions.dateRange === 'filtered') {
    dataToExport = await fetchAllTransactionsForExport({ useCurrentFilters: true });
  } else if (exportOptions.dateRange === 'all') {
    dataToExport = await fetchAllTransactionsForExport({ useCurrentFilters: false });
  } else if (exportOptions.dateRange === 'custom') {
    const customFilters = {
      startDate: exportOptions.customStartDate,
      endDate: exportOptions.customEndDate,
      categoryIds: exportOptions.selectedCategories
    };

    if (exportOptions.selectedTypes.length === 1) {
      customFilters.type = exportOptions.selectedTypes[0];
    }

    dataToExport = await fetchAllTransactionsForExport({
      useCurrentFilters: false,
      customFilters
    });
  }
  
  dataToExport.sort((a, b) => new Date(b.date) - new Date(a.date));
  return dataToExport;
};

 const exportToExcelAdvanced = async () => {
  try {
    // التأكد من تحميل مكتبة XLSX
    await loadSheetJSLibrary();
    
    const dataToExport = await getExportData();
    const exportSummary = calculateSummaryForData(dataToExport);
    
    // إنشاء ملف Excel بصيغة xlsx
    const workbook = createAdvancedExcelFileXLSX(dataToExport, exportSummary, exportOptions, getCategoryName, getPaymentMethodName);
    
    // تصدير الملف
    const XLSX = window.XLSX;
    XLSX.writeFile(workbook, `تقرير_معاملات_الشركة_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast.success('تم تصدير التقرير بصيغة Excel بنجاح!');
  } catch (error) {
    console.error('خطأ في التصدير:', error);
    toast.error('حدث خطأ أثناء التصدير');
  }
};

// دالة إنشاء HTML مع تنسيق Excel كامل
const createExcelHTML = (data, summary) => {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });

  return `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8">
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>تقرير المعاملات</x:Name>
              <x:WorksheetOptions>
                <x:Print>
                  <x:ValidPrinterInfo/>
                  <x:PaperSizeIndex>9</x:PaperSizeIndex>
                  <x:HorizontalResolution>600</x:HorizontalResolution>
                  <x:VerticalResolution>600</x:VerticalResolution>
                  <x:Scale>100</x:Scale>
                  <x:FitToPage/>
                  <x:FitWidth>1</x:FitWidth>
                  <x:FitHeight>32767</x:FitHeight>
                  <x:Orientation>Portrait</x:Orientation>
                  <x:TopMargin>0.75</x:TopMargin>
                  <x:BottomMargin>0.75</x:BottomMargin>
                  <x:LeftMargin>0.7</x:LeftMargin>
                  <x:RightMargin>0.7</x:RightMargin>
                  <x:HeaderMargin>0.3</x:HeaderMargin>
                  <x:FooterMargin>0.3</x:FooterMargin>
                </x:Print>
                <x:Selected/>
                <x:ProtectContents>False</x:ProtectContents>
                <x:ProtectObjects>False</x:ProtectObjects>
                <x:ProtectScenarios>False</x:ProtectScenarios>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        @page {
          size: A4;
          margin: 0.75in 0.7in 0.75in 0.7in;
          mso-page-orientation: portrait;
          mso-header-margin: 0.3in;
          mso-footer-margin: 0.3in;
        }
        
        table { 
          border-collapse: collapse; 
          width: 100%; 
          direction: rtl;
          font-family: Calibri, Arial, sans-serif;
          page-break-inside: avoid;
        }
        
        td {
          border: 2px solid #000000 !important;
          padding: 8px !important;
          vertical-align: middle !important;
          text-align: center !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .header-main {
          background-color: #ADD8E6 !important;
          font-size: 22pt !important;
          font-weight: bold !important;
          text-align: center !important;
          padding: 12px !important;
          height: 45px !important;
          color: #000000 !important;
        }
        
        .summary-row {
          background-color: #ADD8E6 !important;
          font-size: 22pt !important;
          font-weight: bold !important;
          text-align: center !important;
          padding: 12px !important;
          height: 45px !important;
          color: #000000 !important;
        }
        
        .table-header {
          background-color: #D3D3D3 !important;
          font-size: 22pt !important;
          font-weight: bold !important;
          text-align: center !important;
          padding: 10px !important;
          height: 40px !important;
          color: #000000 !important;
        }
        
        .data-cell {
          font-size: 18pt !important;
          text-align: center !important;
          padding: 8px !important;
          height: 35px !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .row-number {
          background-color: #F0F0F0 !important;
          font-weight: bold !important;
          font-size: 18pt !important;
          color: #000000 !important;
        }
        
        .date-cell {
          font-weight: bold !important;
          font-size: 18pt !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
        }
        
        .type-cell {
          font-size: 18pt !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .amount-cell {
          font-weight: bold !important;
          font-size: 18pt !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
        }
        
        @media print {
          table {
            page-break-inside: avoid;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          td {
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <table>
        <tr>
          <td colspan="8" class="header-main">
            تاريخ التقرير: ${formattedDate}
          </td>
        </tr>
        
        <tr>
          <td colspan="8" class="summary-row">
            إجمالي الإيرادات ${summary.totalIncome.toLocaleString()} جنيه / إجمالي المصروفات ${summary.totalExpenses.toLocaleString()} جنيه / صافي الرصيد ${summary.balance.toLocaleString()} جنيه
          </td>
        </tr>
        
        <tr>
          <td class="table-header">م</td>
          <td class="table-header">التاريخ</td>
          <td class="table-header">نوع المعاملة</td>
          <td class="table-header">التصنيف</td>
          <td class="table-header">الوصف</td>
          <td class="table-header">الملاحظات</td>
          <td class="table-header">المبلغ (جنيه)</td>
          <td class="table-header">طريقة الدفع</td>
        </tr>
        
        ${data.map((transaction, index) => {
          return `
            <tr>
              <td class="row-number">${index + 1}</td>
              <td class="date-cell">${formatDateForExcel(transaction.date)}</td>
              <td class="type-cell">${transaction.type === 'income' ? 'إيراد' : 'مصروف'}</td>
              <td class="data-cell">${getCategoryName(transaction.categoryId)}</td>
              <td class="data-cell">${transaction.description || ''}</td>
              <td class="data-cell">${transaction.notes || ''}</td>
              <td class="amount-cell">${parseInt(transaction.amount).toLocaleString()}</td>
              <td class="data-cell">${getPaymentMethodName(transaction.paymentMethodId)}</td>
            </tr>
          `;
        }).join('')}
      </table>
    </body>
    </html>
  `;
};

const exportToCSV = async () => {
  const dataToExport = await getExportData();
  
  let csvContent = "\uFEFF";
  
  csvContent += "تقرير معاملات الشركة\n";
  csvContent += `تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG')}\n`;
  csvContent += `عدد المعاملات: ${dataToExport.length}\n\n`;
  
  if (exportOptions.includeSummary) {
    const exportSummary = calculateSummaryForData(dataToExport);
    csvContent += "الملخص المالي:\n";
    csvContent += `إجمالي الإيرادات,${exportSummary.totalIncome} جنيه\n`;
    csvContent += `إجمالي المصروفات,${exportSummary.totalExpenses} جنيه\n`;
    csvContent += `الرصيد,${exportSummary.balance} جنيه\n\n`;
  }
  
  csvContent += "التاريخ,النوع,التصنيف,الوصف,الملاحظات,المبلغ (جنيه),طريقة الدفع\n";
  
  dataToExport.forEach(transaction => {
    const row = [
      formatDate(transaction.date),
      transaction.type === 'income' ? 'إيراد' : 'مصروف',
      getCategoryName(transaction.categoryId),
      transaction.description || '',
      transaction.notes || '',
      `${transaction.amount} جنيه`,
      getPaymentMethodName(transaction.paymentMethodId)
    ];
    csvContent += row.map(field => `"${field}"`).join(',') + '\n';
  });
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `معاملات_الشركة_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportToPDF = async () => {
  const printWindow = window.open('', '_blank');
  const dataToExport = await getExportData();
  const exportSummary = calculateSummaryForData(dataToExport);
  
  const htmlContent = `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <title>تقرير معاملات الشركة</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          direction: rtl;
          background: white;
          padding: 20px;
          font-size: 12px;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #3b82f6;
          padding-bottom: 20px;
        }
        .company-name { 
          font-size: 24px; 
          font-weight: bold; 
          color: #1e40af;
          margin-bottom: 10px;
        }
        .report-title { 
          font-size: 18px; 
          color: #374151;
          margin-bottom: 10px;
        }
        .report-date { 
          color: #6b7280; 
          font-size: 14px;
        }
        
        .summary-section {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 30px;
        }
        .summary-title {
          font-size: 16px;
          font-weight: bold;
          color: #1e40af;
          margin-bottom: 15px;
          text-align: center;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          text-align: center;
        }
        .summary-item {
          background: white;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }
        .summary-label {
          font-weight: bold;
          color: #374151;
          margin-bottom: 5px;
        }
        .summary-value {
          font-size: 18px;
          font-weight: bold;
        }
        .income { color: #10b981; }
        .expense { color: #ef4444; }
        .balance { color: #3b82f6; }
        
        .table-container {
          margin-top: 20px;
          overflow-x: auto;
        }
        table { 
          width: 100%; 
          border-collapse: collapse;
          background: white;
          border: 1px solid #e2e8f0;
        }
        th, td { 
          padding: 12px 8px; 
          text-align: right;
          border: 1px solid #e2e8f0;
          font-size: 11px;
        }
        th { 
          background: #f1f5f9;
          font-weight: bold;
          color: #334155;
        }
        tr:nth-child(even) { background: #f8fafc; }
        
        .type-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: bold;
        }
        .income-badge { 
          background: #d1fae5; 
          color: #065f46; 
        }
        .expense-badge { 
          background: #fee2e2; 
          color: #991b1b; 
        }
        
        .amount {
          font-weight: bold;
          font-size: 12px;
        }
        .amount.income { color: #10b981; }
        .amount.expense { color: #ef4444; }
        
        .footer {
          margin-top: 30px;
          text-align: center;
          color: #6b7280;
          font-size: 10px;
          border-top: 1px solid #e2e8f0;
          padding-top: 15px;
        }
        
        @media print {
          body { padding: 10px; }
          .header { margin-bottom: 20px; }
          .summary-section { margin-bottom: 20px; }
          th, td { padding: 8px 6px; font-size: 10px; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">معاملات الشركة</div>
        <div class="report-title">تقرير مالي شامل</div>
        <div class="report-date">
          تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })} | 
          عدد المعاملات: ${dataToExport.length}
        </div>
      </div>

      ${exportOptions.includeSummary ? `
      <div class="summary-section">
        <div class="summary-title">الملخص المالي</div>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-label">إجمالي الإيرادات</div>
            <div class="summary-value income">${formatAmount(exportSummary.totalIncome)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">إجمالي المصروفات</div>
            <div class="summary-value expense">${formatAmount(exportSummary.totalExpenses)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">الرصيد</div>
            <div class="summary-value balance">${formatAmount(exportSummary.balance)}</div>
          </div>
        </div>
      </div>
      ` : ''}

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th style="width: 80px;">التاريخ</th>
              <th style="width: 60px;">النوع</th>
              <th style="width: 80px;">التصنيف</th>
              <th style="width: 120px;">الوصف</th>
              <th style="width: 120px;">الملاحظات</th>
              <th style="width: 80px;">المبلغ</th>
              <th style="width: 80px;">طريقة الدفع</th>
            </tr>
          </thead>
          <tbody>
            ${dataToExport.map(transaction => `
              <tr>
                <td>${formatDate(transaction.date)}</td>
                <td>
                  <span class="type-badge ${transaction.type === 'income' ? 'income-badge' : 'expense-badge'}">
                    ${transaction.type === 'income' ? 'إيراد' : 'مصروف'}
                  </span>
                </td>
                <td>${getCategoryName(transaction.categoryId)}</td>
                <td>${transaction.description || '-'}</td>
                <td>${transaction.notes || '-'}</td>
                <td>
                  <span class="amount ${transaction.type}">
                    ${formatAmount(transaction.amount)}
                  </span>
                </td>
                <td>${getPaymentMethodName(transaction.paymentMethodId)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="footer">
        تم إنشاء هذا التقرير بواسطة نظام إدارة معاملات الشركة | 
        ${new Date().toLocaleString('ar-EG')}
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
};

 // حساب الملخص للبيانات المحددة
 const calculateSummaryForData = (data) => {
   const totalIncome = data
     .filter(t => t.type === 'income')
     .reduce((sum, t) => sum + parseFloat(t.amount), 0);
   
   const totalExpenses = data
     .filter(t => t.type === 'expense')
     .reduce((sum, t) => sum + parseFloat(t.amount), 0);
   
   return {
     totalIncome,
     totalExpenses,
     balance: totalIncome - totalExpenses
   };
 };

 // تصدير التقرير الرئيسي
 const exportReport = () => {
   setIsExportModalOpen(true);
 };

 // تنفيذ التصدير حسب النوع المختار
const executeExport = async () => {
  if (exportOptions.format === 'excel') {
    await exportToExcelAdvanced();
  } else if (exportOptions.format === 'csv') {
    await exportToCSV();
  } else if (exportOptions.format === 'pdf') {
    await exportToPDF();
  }
  setIsExportModalOpen(false);
};

// استبدل دالة printReport الحالية بهذه النسخة المعدلة
const printReport = async () => {
  // استخدام البيانات المعروضة حاليًا فقط (30 معاملة)
  const dataToExport = paginatedTransactions;

  const printSummary = calculateSummaryForData(dataToExport);
  
  const printWindow = window.open('', '_blank');
  
  const htmlContent = `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <title>تقرير معاملات الشركة - الصفحة ${virtualPagination.currentPage}</title>
      <style>
        @page {
          size: A4;
          margin: 10mm 5mm;
        }
        
        * { 
          margin: 0; 
          padding: 0; 
          box-sizing: border-box; 
        }
        
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          direction: rtl;
          background: white;
          color: black;
          font-size: 16px;
          line-height: 1.4;
        }
        
        .print-container {
          max-width: 100%;
          margin: 0;
          background: white;
          padding: 8px;
        }
        
        .report-header {
          text-align: center;
          margin-bottom: 20px;
          border-bottom: 3px solid black;
          padding-bottom: 15px;
        }
        
        .company-name {
          font-size: 32px;
          font-weight: bold;
          color: black;
          margin-bottom: 8px;
        }
        
        .report-title {
          font-size: 24px;
          color: black;
          margin-bottom: 8px;
        }
        
        .report-date {
          font-size: 18px;
          color: black;
        }
        
        .page-info {
          font-size: 16px;
          color: black;
          margin-top: 5px;
          font-weight: bold;
        }
        
        .summary-section {
          background: white;
          border: 2px solid black;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
        }
        
        .summary-title {
          font-size: 24px;
          font-weight: bold;
          color: black;
          margin-bottom: 15px;
          text-align: center;
          border-bottom: 1px solid black;
          padding-bottom: 8px;
        }
        
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          text-align: center;
        }
        
        .summary-item {
          background: white;
          padding: 12px;
          border: 1px solid black;
          border-radius: 6px;
        }
        
        .summary-label {
          font-weight: bold;
          color: black;
          margin-bottom: 6px;
          font-size: 16px;
        }
        
        .summary-value {
          font-size: 22px;
          font-weight: bold;
          color: black;
        }
        
        .table-container {
          margin-top: 15px;
          overflow-x: auto;
        }
        
        table { 
          width: 100%; 
          border-collapse: collapse;
          background: white;
          border: 2px solid black;
        }
        
        th, td { 
          padding: 10px 6px;
          text-align: center;
          border: 1px solid black;
          font-size: 14px;
          color: black;
        }
        
        th { 
          background: #f0f0f0;
          font-weight: bold;
          color: black;
          font-size: 16px;
        }
        
        tr:nth-child(even) { 
          background: #f8f8f8; 
        }
        
        tr:nth-child(odd) { 
          background: white; 
        }
        
        .row-number {
          font-weight: bold;
          background: #e0e0e0;
          font-size: 14px;
        }
        
        .date-cell {
          font-weight: bold;
          font-size: 14px;
        }
        
        .type-cell {
          font-weight: bold;
          font-size: 14px;
        }
        
        .amount-cell {
          font-weight: bold;
          font-size: 14px;
        }
        
        .footer {
          margin-top: 20px;
          text-align: center;
          color: black;
          font-size: 14px;
          border-top: 1px solid black;
          padding-top: 10px;
        }
        
        @media print {
          body { 
            font-size: 14px;
          }
          
          .print-container {
            padding: 5px;
          }
          
          .company-name {
            font-size: 28px;
          }
          
          .report-title {
            font-size: 22px;
          }
          
          .report-date {
            font-size: 16px;
          }
          
          .summary-title {
            font-size: 22px;
          }
          
          .summary-label {
            font-size: 14px;
          }
          
          .summary-value {
            font-size: 20px;
          }
          
          th, td { 
            padding: 8px 4px; 
            font-size: 12px;
          }
          
          th {
            font-size: 14px;
          }
          
          .row-number, .date-cell, .type-cell, .amount-cell {
            font-size: 12px;
          }
          
          table {
            page-break-inside: avoid;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          @page {
            margin: 8mm 3mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="report-header">
          <div class="company-name">معاملات الشركة</div>
          <div class="report-title">تقرير مالي شامل</div>
          <div class="report-date">
            تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'long'
            })}
          </div>
          <div class="page-info">
            الصفحة ${virtualPagination.currentPage} من ${virtualPaginationInfo.totalPages} | 
            عرض ${dataToExport.length} معاملة من أصل ${virtualPaginationInfo.totalCount}
          </div>
        </div>

        <div class="summary-section">
          <div class="summary-title">ملخص الصفحة الحالية</div>
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">إجمالي الإيرادات</div>
              <div class="summary-value">${formatAmount(printSummary.totalIncome)}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">إجمالي المصروفات</div>
              <div class="summary-value">${formatAmount(printSummary.totalExpenses)}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">الرصيد</div>
              <div class="summary-value">${formatAmount(printSummary.balance)}</div>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th style="width: 40px;">م</th>
                <th style="width: 90px;">التاريخ</th>
                <th style="width: 70px;">النوع</th>
                <th style="width: 90px;">التصنيف</th>
                <th style="width: 140px;">الوصف</th>
                <th style="width: 140px;">الملاحظات</th>
                <th style="width: 90px;">المبلغ (جنيه)</th>
                <th style="width: 110px;">طريقة الدفع</th>
              </tr>
            </thead>
            <tbody>
              ${dataToExport.map((transaction, index) => `
                <tr>
                  <td class="row-number">${(virtualPagination.currentPage - 1) * virtualPagination.itemsPerPage + index + 1}</td>
                  <td class="date-cell">${formatDate(transaction.date)}</td>
                  <td class="type-cell">${transaction.type === 'income' ? 'إيراد' : 'مصروف'}</td>
                  <td>${getCategoryName(transaction.categoryId)}</td>
                  <td>${transaction.description || '-'}</td>
                  <td>${transaction.notes || '-'}</td>
                  <td class="amount-cell">${parseInt(transaction.amount).toLocaleString()}</td>
                  <td>${getPaymentMethodName(transaction.paymentMethodId)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="footer">
          تم إنشاء هذا التقرير بواسطة نظام إدارة معاملات الشركة | 
          ${new Date().toLocaleString('ar-EG')}
        </div>
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
};

 // إضافة معاملة جديدة
 const handleAddTransaction = async () => {
   try {
     if (!transactionForm.amount || !transactionForm.categoryId || !transactionForm.paymentMethodId) {
       toast.error('يرجى ملء جميع الحقول المطلوبة');
       return;
     }
     
     setLoading(true);
     
     const response = await axios.post(`${config.API_BASE_URL}/api/company-transactions`, {
       ...transactionForm,
       amount: parseFloat(transactionForm.amount),
       createdBy: getCurrentUser()
     });
     
     if (response.data.success) {
       toast.success('تمت إضافة المعاملة بنجاح');
       resetTransactionForm();
       await fetchData();
     }
   } catch (err) {
     console.error('خطأ في إضافة المعاملة:', err);
     toast.error('حدث خطأ أثناء إضافة المعاملة');
   } finally {
     setLoading(false);
   }
 };
 
 // تعديل معاملة
 const handleEditTransaction = async () => {
   try {
     if (!transactionForm.amount || !transactionForm.categoryId || !transactionForm.paymentMethodId) {
       toast.error('يرجى ملء جميع الحقول المطلوبة');
       return;
     }
     
     setLoading(true);
     
     const response = await axios.put(
       `${config.API_BASE_URL}/api/company-transactions/${editingTransaction.id}/sync-update`,
       {
         ...transactionForm,
         amount: parseFloat(transactionForm.amount)
       }
     );
     
     if (response.data.success) {
       toast.success('تم تحديث المعاملة بنجاح مع المزامنة');
       resetTransactionForm();
       await fetchData();
     }
   } catch (err) {
     console.error('خطأ في تحديث المعاملة:', err);
     toast.error('حدث خطأ أثناء تحديث المعاملة');
   } finally {
     setLoading(false);
   }
 };
 
 // حذف معاملة
 const handleDeleteTransaction = async (id) => {
   if (!window.confirm('هل أنت متأكد من حذف هذه المعاملة؟')) return;
   
   try {
     setLoading(true);
     
     const response = await axios.delete(`${config.API_BASE_URL}/api/company-transactions/${id}`);
     
     if (response.data.success) {
       toast.success('تم حذف المعاملة بنجاح');
       await fetchData();
     }
   } catch (err) {
     console.error('خطأ في حذف المعاملة:', err);
     toast.error('حدث خطأ أثناء حذف المعاملة');
   } finally {
     setLoading(false);
   }
 };
 
 // إضافة تصنيف جديد
 const handleAddCategory = async () => {
   try {
     if (!categoryForm.name) {
       toast.error('يرجى إدخال اسم التصنيف');
       return;
     }
     
     setLoading(true);
     
     const response = await axios.post(`${config.API_BASE_URL}/api/transaction-categories`, categoryForm);
     
     if (response.data.success) {
       toast.success('تم إضافة التصنيف بنجاح');
       resetCategoryForm();
       await fetchCategories();
     }
   } catch (err) {
     console.error('خطأ في إضافة التصنيف:', err);
     toast.error('حدث خطأ أثناء إضافة التصنيف');
   } finally {
     setLoading(false);
   }
 };
 
 // تعديل تصنيف
const handleEditCategory = async () => {
 try {
   if (!categoryForm.name) {
     toast.error('يرجى إدخال اسم التصنيف');
     return;
   }
   
   setLoading(true);
   
   const response = await axios.put(
     `${config.API_BASE_URL}/api/transaction-categories/${editingCategory.id}`,
     categoryForm
   );
   
   if (response.data.success) {
     toast.success('تم تحديث التصنيف بنجاح');
     resetCategoryForm();
     await fetchCategories();
   }
 } catch (err) {
   console.error('خطأ في تحديث التصنيف:', err);
   toast.error('حدث خطأ أثناء تحديث التصنيف');
 } finally {
   setLoading(false);
 }
};
 
 // حذف تصنيف
const handleDeleteCategory = async (id) => {
 if (!window.confirm('هل أنت متأكد من حذف هذا التصنيف؟')) return;
 
 try {
   setLoading(true);
   
   const response = await axios.delete(`${config.API_BASE_URL}/api/transaction-categories/${id}`);
   
   if (response.data.success) {
     toast.success('تم حذف التصنيف بنجاح');
     await fetchCategories();
   }
 } catch (err) {
   console.error('خطأ في حذف التصنيف:', err);
   toast.error(err.response?.data?.error || 'حدث خطأ أثناء حذف التصنيف');
 } finally {
   setLoading(false);
 }
};
 
 // إعادة تعيين نموذج المعاملة
// إعادة تعيين نموذج المعاملة
const resetTransactionForm = () => {
  setTransactionForm({
    type: 'income',
    amount: '',
    categoryId: '',
    paymentMethodId: '',
    paymentMethodType: '',
    description: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  setCategorySearch('');
  setShowCategoryDropdown(false);
  setEditingTransaction(null);
  setIsTransactionModalOpen(false);
};
 
 // إعادة تعيين نموذج التصنيف
 const resetCategoryForm = () => {
   setCategoryForm({
     name: '',
     type: 'expense',
     description: ''
   });
   setEditingCategory(null);
   setIsCategoryModalOpen(false);
 };
 
 // بدء تعديل معاملة
// بدء تعديل معاملة
const startEditTransaction = (transaction) => {
  setEditingTransaction(transaction);
  setTransactionForm({
    type: transaction.type,
    amount: transaction.amount,
    categoryId: transaction.categoryId,
    paymentMethodId: transaction.paymentMethodId,
    paymentMethodType: transaction.paymentMethodType,
    description: transaction.description || '',
    notes: transaction.notes || '',
    date: new Date(transaction.date).toISOString().split('T')[0]
  });
  setCategorySearch(getCategoryName(transaction.categoryId));
  setShowCategoryDropdown(false);
  setIsTransactionModalOpen(true);
};
 
 // بدء تعديل تصنيف
 const startEditCategory = (category) => {
   setEditingCategory(category);
   setCategoryForm({
     name: category.name,
     type: category.type,
     description: category.description || ''
   });
   setIsCategoryModalOpen(true);
 };
 
 // تحديث طريقة الدفع عند تغييرها
const handlePaymentMethodChange = (e) => {
  const selectedMethod = paymentMethods.find(pm => 
    pm.id === parseInt(e.target.value) && pm.type === 'bank'
  );
  if (selectedMethod) {
    setTransactionForm({
      ...transactionForm,
      paymentMethodId: selectedMethod.id,
      paymentMethodType: 'bank'
    });
  }
};
 
// البيانات المعروضة تأتي جاهزة من السيرفر حسب الصفحة والفلاتر الحالية
const getFilteredTransactions = useMemo(() => {
  return transactions;
}, [transactions]);

const paginatedTransactions = useMemo(() => {
  return getFilteredTransactions;
}, [getFilteredTransactions]);

// معلومات الصفحة الحالية من السيرفر
const virtualPaginationInfo = useMemo(() => ({
  totalCount: pagination.totalCount || 0,
  totalPages: Math.max(1, pagination.totalPages || 1),
  currentPage: pagination.currentPage || virtualPagination.currentPage,
  hasMore: pagination.hasMore,
  itemsPerPage: pagination.limit || virtualPagination.itemsPerPage
}), [pagination, virtualPagination.currentPage, virtualPagination.itemsPerPage]);
 
 // الحصول على اسم التصنيف
 const getCategoryName = (categoryId) => {
   const category = categories.find(c => c.id === categoryId);
   return category ? category.name : 'غير محدد';
 };
 
 // الحصول على اسم طريقة الدفع
const getPaymentMethodName = (paymentMethodId) => {
  const method = paymentMethods.find(pm => pm.id === paymentMethodId && pm.type === 'bank');
  return method ? method.Name : 'غير محدد';
};
 

const getFilteredCategories = () => {
  return categories
    .filter(cat => cat.type === transactionForm.type)
    .filter(cat => 
      categorySearch === '' || 
      cat.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
};

// اختيار تصنيف
const selectCategory = (category) => {
  setTransactionForm({...transactionForm, categoryId: category.id});
  setCategorySearch(category.name);
  setShowCategoryDropdown(false);
};

// مسح البحث
const clearCategorySearch = () => {
  setCategorySearch('');
  setTransactionForm({...transactionForm, categoryId: ''});
  setShowCategoryDropdown(false);
};

 // تنسيق المبلغ
const formatAmount = (amount) => {
  return new Intl.NumberFormat('ar-EG', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};
 
 // تنسيق التاريخ
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    calendar: 'gregory'
  });
};
 

 return (
   <div className="min-h-screen bg-gray-50 py-6 max-md:py-2 max-md:overflow-x-hidden" dir="rtl">
     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-md:px-3 max-md:max-w-full">
       <div className="print-area">
         {/* رأس الصفحة */}
         <div className="bg-white rounded-lg shadow-md p-6 mb-6 max-md:rounded-2xl max-md:p-4 max-md:mb-4 max-md:sticky max-md:top-0 max-md:z-20 max-md:shadow-lg">
           <div className="flex justify-between items-center max-md:gap-3 max-md:items-start">
             <h1 className="text-2xl font-bold text-gray-900 flex items-center max-md:text-xl max-md:leading-snug">
               <Building className="text-blue-600 ml-2 max-md:w-6 max-md:h-6" size={28} />
               إيرادات ومصروفات الشركة
             </h1>
             <button
               onClick={() => navigate('/')}
               className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2.5 rounded-lg transition duration-200 no-print max-md:px-3 max-md:py-2 max-md:text-sm max-md:rounded-xl max-md:shrink-0"
             >
               العودة للرئيسية
             </button>
           </div>
           
           {/* مؤشر عدد المعاملات المعروضة */}
        {/* مؤشر عدد المعاملات المعروضة */}
<div className="mt-4 flex items-center justify-between text-sm text-gray-600 max-md:flex-col max-md:items-start max-md:gap-1 max-md:bg-gray-50 max-md:rounded-xl max-md:p-3">
  <div>
    عرض {paginatedTransactions.length} من أصل {virtualPaginationInfo.totalCount} معاملة
    {(search.trim() || filterType !== 'all' || filterCategory !== 'all' || filterPaymentMethod !== 'all' || dateRange.start || dateRange.end) && (
      <span className="text-blue-600 font-medium mr-2">(نتائج حسب الفلاتر الحالية)</span>
    )}
  </div>
  {search.trim() && (
    <div className="text-sm text-green-600 flex items-center">
      <span className="mr-1">🔎</span>
      بحث من السيرفر
    </div>
  )}
</div>
         </div>
         
         {/* بطاقات الملخص */}
         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 max-md:grid-cols-3 max-md:gap-2 max-md:mb-4">
           {/* إجمالي الإيرادات */}
           <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white max-md:p-3 max-md:rounded-2xl">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-green-100 text-sm max-md:text-[11px] max-md:leading-tight">إجمالي الإيرادات</p>
                 <p className="text-3xl font-bold mt-2 max-md:text-sm max-md:mt-1 max-md:leading-tight">{formatAmount(summary.totalIncome)}</p>
               </div>
               <TrendingUp size={40} className="text-green-200 max-md:hidden" />
             </div>
           </div>
           
           {/* إجمالي المصروفات */}
           <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white max-md:p-3 max-md:rounded-2xl">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-red-100 text-sm max-md:text-[11px] max-md:leading-tight">إجمالي المصروفات</p>
                 <p className="text-3xl font-bold mt-2 max-md:text-sm max-md:mt-1 max-md:leading-tight">{formatAmount(summary.totalExpenses)}</p>
               </div>
               <TrendingDown size={40} className="text-red-200 max-md:hidden" />
             </div>
           </div>
           
           {/* الرصيد */}
           <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white max-md:p-3 max-md:rounded-2xl">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-blue-100 text-sm max-md:text-[11px] max-md:leading-tight">الرصيد</p>
                 <p className="text-3xl font-bold mt-2 max-md:text-sm max-md:mt-1 max-md:leading-tight">{formatAmount(summary.balance)}</p>
               </div>
               <Wallet size={40} className="text-blue-200 max-md:hidden" />
             </div>
           </div>
         </div>
         
     {/* أدوات التحكم */}
<div className="bg-white rounded-lg shadow-md p-6 mb-6 no-print max-md:p-4 max-md:rounded-2xl max-md:mb-4 max-md:shadow-lg">
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 max-md:gap-3">
    {/* البحث */}
    <div className="relative">
      <input
        type="text"
        placeholder="البحث في المعاملات..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); resetServerPage(); }}
        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
      <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
    </div>
    
    {/* فلتر النوع */}
    <select
      value={filterType}
      onChange={(e) => { setFilterType(e.target.value); resetServerPage(); }}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="all">جميع المعاملات</option>
      <option value="income">الإيرادات فقط</option>
      <option value="expense">المصروفات فقط</option>
    </select>
    
    {/* فلتر التصنيف */}
    <select
      value={filterCategory}
      onChange={(e) => { setFilterCategory(e.target.value); resetServerPage(); }}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="all">جميع التصنيفات</option>
      {categories.map(category => (
        <option key={category.id} value={category.id}>
          {category.name} ({category.type === 'income' ? 'إيراد' : 'مصروف'})
        </option>
      ))}
    </select>
    
    {/* فلتر طريقة الدفع */}
    <select
      value={filterPaymentMethod}
      onChange={(e) => { setFilterPaymentMethod(e.target.value); resetServerPage(); }}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="all">جميع الحسابات البنكية</option>
      {paymentMethods
        .filter(method => method.type === 'bank')
        .map(method => (
          <option key={method.id} value={method.id}>
            {method.Name}
          </option>
        ))
      }
    </select>
  </div>
  
  {/* فلتر التاريخ والأزرار مع خيار عدد العناصر */}
  <div className="flex flex-wrap gap-4 items-center justify-between max-md:block">
    <div className="flex gap-4 max-md:grid max-md:grid-cols-2 max-md:gap-3">
      <div>
        <label className="block text-sm text-gray-600 mb-1">من تاريخ</label>
        <input
          type="date"
          value={dateRange.start}
          onChange={(e) => { setDateRange({...dateRange, start: e.target.value}); resetServerPage(); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-md:w-full max-md:min-w-0"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-600 mb-1">إلى تاريخ</label>
        <input
          type="date"
          value={dateRange.end}
          onChange={(e) => { setDateRange({...dateRange, end: e.target.value}); resetServerPage(); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-md:w-full max-md:min-w-0"
        />
      </div>
      
      {/* خيار عدد العناصر في الصفحة */}
      <div>
        <label className="block text-sm text-gray-600 mb-1">عرض في الصفحة</label>
        <select
          value={virtualPagination.itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[120px] max-md:w-full max-md:min-w-0"
        >
          {itemsPerPageOptions.map(option => (
            <option key={option} value={option}>
              {option} معاملة
            </option>
          ))}
        </select>
      </div>
    </div>
    
    <div className="flex gap-2 max-md:grid max-md:grid-cols-2 max-md:gap-2 max-md:mt-4">
      {canDo('إضافة معاملة') && (
      <button
        onClick={() => setIsTransactionModalOpen(true)}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 max-md:justify-center max-md:rounded-xl max-md:py-3"
      >
        <Plus size={20} />
        إضافة معاملة
      </button>
      )}
      {canDo('إدارة التصنيفات') && (
      <button
        onClick={() => setIsCategoryModalOpen(true)}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 max-md:justify-center max-md:rounded-xl max-md:py-3"
      >
        <Tag size={20} />
        إدارة التصنيفات
      </button>
      )}
      <button
        onClick={exportReport}
        className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 max-md:justify-center max-md:rounded-xl max-md:py-3"
      >
        <FileSpreadsheet size={20} />
        تصدير متقدم
      </button>
      <button
        onClick={printReport}
        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 max-md:justify-center max-md:rounded-xl max-md:py-3"
      >
        <Printer size={20} />
        طباعة
      </button>
      <button
        onClick={fetchData}
        className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg max-md:rounded-xl max-md:py-3 max-md:flex max-md:items-center max-md:justify-center"
        title="تحديث البيانات"
      >
        <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
      </button>
    </div>
  </div>
</div>
         
         {/* جدول المعاملات */}
         <div className="bg-white rounded-lg shadow-md overflow-hidden">
           {loading ? (
             <div className="text-center py-10">
               <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
               <p className="text-gray-500">جاري تحميل البيانات...</p>
             </div>
           ) : error ? (
             <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4">
               <div className="flex items-center">
                 <AlertCircle className="text-red-500 ml-3" />
                 <p className="text-red-700">{error}</p>
               </div>
             </div>
         ) : paginatedTransactions.length === 0 ? (
          <div className="text-center py-10">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">لا توجد معاملات</h3>
            <p className="text-gray-500">لم يتم العثور على أي معاملات تطابق معايير البحث</p>
          </div>
           ) : (
             <>
             <div className="hidden md:block overflow-x-auto">
               <table className="min-w-full divide-y divide-gray-200">
                 <thead className="bg-gray-50">
                   <tr>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                       التاريخ
                     </th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                       النوع
                     </th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                       التصنيف
                     </th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                       الوصف
                     </th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                       المبلغ
                     </th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                       طريقة الدفع
                     </th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                       أضيف بواسطة
                     </th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase no-print">
                       الإجراءات
                     </th>
                   </tr>
                 </thead>
                 <tbody className="bg-white divide-y divide-gray-200">
  {paginatedTransactions.map((transaction) => (
    <tr key={transaction.id} className="hover:bg-gray-50">
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div className="text-sm text-gray-900">{formatDate(transaction.date)}</div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                           transaction.type === 'income' 
                             ? 'bg-green-100 text-green-800' 
                             : 'bg-red-100 text-red-800'
                         }`}>
                           {transaction.type === 'income' ? (
                             <>
                               <TrendingUp size={14} className="ml-1" />
                               إيراد
                             </>
                           ) : (
                             <>
                               <TrendingDown size={14} className="ml-1" />
                               مصروف
                             </>
                           )}
                         </span>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <span className="text-sm text-gray-900">
                           {getCategoryName(transaction.categoryId)}
                         </span>
                       </td>
                       <td className="px-6 py-4">
                         <div className="text-sm text-gray-900">{transaction.description}</div>
                         {transaction.notes && (
                           <div className="text-xs text-gray-500 mt-1">{transaction.notes}</div>
                         )}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <span className={`text-sm font-medium ${
                           transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                         }`}>
                           {formatAmount(transaction.amount)}
                         </span>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <span className="text-sm text-gray-900">
                           {getPaymentMethodName(transaction.paymentMethodId)}
                         </span>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div className="flex items-center gap-2">
                           <User size={15} className="text-gray-400 shrink-0" />
                           <span className="text-sm text-gray-700 font-medium">
                             {transaction.createdByFullName || transaction.createdBy || '-'}
                           </span>
                         </div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-center no-print">
                         <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse">
                           {canDo('معاينة المعاملة') && (
                           <button
                             onClick={() => setViewingTransaction(transaction)}
                             className="text-blue-600 hover:text-blue-900 p-1"
                             title="عرض التفاصيل"
                           >
                             <Eye size={18} />
                           </button>
                           )}
                           {canDo('تعديل المعاملة') && (
                           <button
                             onClick={() => startEditTransaction(transaction)}
                             className="text-yellow-600 hover:text-yellow-900 p-1"
                             title="تعديل المعاملة"
                           >
                             <Edit size={18} />
                           </button>
                           )}
                           {canDo('حذف المعاملة') && (
                           <button
                             onClick={() => handleDeleteTransaction(transaction.id)}
                             className="text-red-600 hover:text-red-900 p-1"
                             title="حذف"
                           >
                             <Trash2 size={18} />
                           </button>
                           )}
                         </div>
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
               
         
             </div>

             {/* كروت الموبايل للمعاملات - تظهر على الهاتف فقط ولا تؤثر على جدول الكمبيوتر */}
             <div className="md:hidden p-3 space-y-3 bg-gray-50">
               {paginatedTransactions.map((transaction) => (
                 <div
                   key={`mobile-${transaction.id}`}
                   className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${
                     transaction.type === 'income' ? 'border-green-100' : 'border-red-100'
                   }`}
                 >
                   <div className={`h-1.5 ${transaction.type === 'income' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                   <div className="p-4">
                     <div className="flex items-start justify-between gap-3 mb-3">
                       <div className="min-w-0">
                         <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                           transaction.type === 'income'
                             ? 'bg-green-50 text-green-700'
                             : 'bg-red-50 text-red-700'
                         }`}>
                           {transaction.type === 'income' ? <TrendingUp size={14} className="ml-1" /> : <TrendingDown size={14} className="ml-1" />}
                           {transaction.type === 'income' ? 'إيراد' : 'مصروف'}
                         </span>
                         <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                           <Calendar size={13} />
                           {formatDate(transaction.date)}
                         </p>
                       </div>
                       <div className={`text-lg font-extrabold shrink-0 ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                         {formatAmount(transaction.amount)}
                       </div>
                     </div>

                     <div className="bg-gray-50 rounded-2xl p-3 mb-3">
                       <div className="text-xs text-gray-400 mb-1">الوصف</div>
                       <div className="text-sm font-semibold text-gray-900 break-words">{transaction.description || 'بدون وصف'}</div>
                       {transaction.notes && (
                         <div className="text-xs text-gray-500 mt-2 break-words">{transaction.notes}</div>
                       )}
                     </div>

                     <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                       <div className="bg-gray-50 rounded-xl p-3">
                         <div className="text-xs text-gray-400 mb-1">التصنيف</div>
                         <div className="font-semibold text-gray-800 break-words">{getCategoryName(transaction.categoryId)}</div>
                       </div>
                       <div className="bg-gray-50 rounded-xl p-3">
                         <div className="text-xs text-gray-400 mb-1">الحساب</div>
                         <div className="font-semibold text-gray-800 break-words">{getPaymentMethodName(transaction.paymentMethodId)}</div>
                       </div>
                       <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                         <div className="text-xs text-gray-400 mb-1">أضيف بواسطة</div>
                         <div className="font-semibold text-gray-800 flex items-center gap-2">
                           <User size={14} className="text-gray-400" />
                           {transaction.createdByFullName || transaction.createdBy || '-'}
                         </div>
                       </div>
                     </div>

                     <div className="grid grid-cols-3 gap-2 no-print">
                       {canDo('معاينة المعاملة') && (
                         <button
                           onClick={() => setViewingTransaction(transaction)}
                           className="bg-blue-50 text-blue-700 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1"
                         >
                           <Eye size={16} />
                           عرض
                         </button>
                       )}
                       {canDo('تعديل المعاملة') && (
                         <button
                           onClick={() => startEditTransaction(transaction)}
                           className="bg-yellow-50 text-yellow-700 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1"
                         >
                           <Edit size={16} />
                           تعديل
                         </button>
                       )}
                       {canDo('حذف المعاملة') && (
                         <button
                           onClick={() => handleDeleteTransaction(transaction.id)}
                           className="bg-red-50 text-red-700 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1"
                         >
                           <Trash2 size={16} />
                           حذف
                         </button>
                       )}
                     </div>
                   </div>
                 </div>
               ))}
             </div>
             </>
           )}
         </div>
       </div>
     </div>
     
     {/* نافذة التصدير المتقدم */}
     {isExportModalOpen && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-md:items-end">
         <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl p-6 m-4 max-h-[90vh] overflow-y-auto max-md:m-0 max-md:max-w-none max-md:h-[92dvh] max-md:max-h-[92dvh] max-md:rounded-t-3xl max-md:rounded-b-none max-md:p-4">
           <h2 className="text-2xl font-bold mb-6 flex items-center max-md:text-xl max-md:sticky max-md:top-0 max-md:bg-white max-md:z-10 max-md:py-3 max-md:mb-4">
             <Settings className="text-blue-600 ml-2" />
             خيارات التصدير المتقدمة
           </h2>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-md:gap-4">
             {/* نوع التصدير */}
             <div>
               <h3 className="text-lg font-medium mb-3">نوع التصدير</h3>
               <div className="space-y-2">
                 <label className="flex items-center">
                   <input
                     type="radio"
                     value="excel"
                     checked={exportOptions.format === 'excel'}
                     onChange={(e) => setExportOptions({...exportOptions, format: e.target.value})}
                     className="ml-2"
                   />
                   <FileSpreadsheet size={18} className="ml-2 text-green-600" />
                   ملف Excel متقدم (.xlsx) - مُوصى به
                 </label>
          
                 <label className="flex items-center">
                   <input
                     type="radio"
                     value="pdf"
                     checked={exportOptions.format === 'pdf'}
                     onChange={(e) => setExportOptions({...exportOptions, format: e.target.value})}
                     className="ml-2"
                   />
                   <FileText size={18} className="ml-2 text-red-600" />
                   ملف PDF
                 </label>
               </div>
             </div>
             
             <div>
  <h3 className="text-lg font-medium mb-3">نطاق البيانات</h3>
  <div className="space-y-2">
    <label className="flex items-center">
      <input
        type="radio"
        value="current"
        checked={exportOptions.dateRange === 'current'}
        onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
        className="ml-2"
      />
      <span className="text-blue-600 font-medium">البيانات المعروضة حاليًا ({paginatedTransactions.length} معاملة)</span>
    </label>
    <label className="flex items-center">
      <input
        type="radio"
        value="filtered"
        checked={exportOptions.dateRange === 'filtered'}
        onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
        className="ml-2"
      />
      جميع المعاملات المفلترة ({virtualPaginationInfo.totalCount} معاملة)
    </label>
    <label className="flex items-center">
      <input
        type="radio"
        value="all"
        checked={exportOptions.dateRange === 'all'}
        onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
        className="ml-2"
      />
      جميع معاملات قاعدة البيانات (يتم تحميلها عند التصدير)
    </label>
    <label className="flex items-center">
      <input
        type="radio"
        value="custom"
        checked={exportOptions.dateRange === 'custom'}
        onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
        className="ml-2"
      />
      فترة مخصصة
    </label>
  </div>
               {/* التاريخ المخصص */}
               {exportOptions.dateRange === 'custom' && (
                 <div className="mt-3 space-y-2">
                   <div>
                     <label className="block text-sm text-gray-600 mb-1">من تاريخ</label>
                     <input
                       type="date"
                       value={exportOptions.customStartDate}
                       onChange={(e) => setExportOptions({...exportOptions, customStartDate: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                     />
                   </div>
                   <div>
                     <label className="block text-sm text-gray-600 mb-1">إلى تاريخ</label>
                     <input
                       type="date"
                       value={exportOptions.customEndDate}
                       onChange={(e) => setExportOptions({...exportOptions, customEndDate: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                     />
                   </div>
                 </div>
               )}
             </div>
             
             {/* فلتر الأنواع */}
             <div>
               <h3 className="text-lg font-medium mb-3">أنواع المعاملات</h3>
               <div className="space-y-2">
                 <label className="flex items-center">
                   <input
                     type="checkbox"
                     checked={exportOptions.selectedTypes.includes('income')}
                     onChange={(e) => {
                       if (e.target.checked) {
                         setExportOptions({
                           ...exportOptions,
                           selectedTypes: [...exportOptions.selectedTypes, 'income']
                         });
                       } else {
                         setExportOptions({
                           ...exportOptions,
                           selectedTypes: exportOptions.selectedTypes.filter(t => t !== 'income')
                         });
                       }
                     }}
                     className="ml-2"
                   />
                   <TrendingUp size={16} className="ml-1 text-green-600" />
                   الإيرادات
                 </label>
                 <label className="flex items-center">
                   <input
                     type="checkbox"
                     checked={exportOptions.selectedTypes.includes('expense')}
                     onChange={(e) => {
                       if (e.target.checked) {
                         setExportOptions({
                           ...exportOptions,
                           selectedTypes: [...exportOptions.selectedTypes, 'expense']
                         });
                       } else {
                         setExportOptions({
                           ...exportOptions,
                           selectedTypes: exportOptions.selectedTypes.filter(t => t !== 'expense')
                         });
                       }
                     }}
                     className="ml-2"
                   />
                   <TrendingDown size={16} className="ml-1 text-red-600" />
                   المصروفات
                 </label>
               </div>
             </div>
             
             {/* خيارات إضافية */}
             <div>
               <h3 className="text-lg font-medium mb-3">خيارات إضافية</h3>
               <div className="space-y-2">
                 <label className="flex items-center">
                   <input
                     type="checkbox"
                     checked={exportOptions.includeSummary}
                     onChange={(e) => setExportOptions({...exportOptions, includeSummary: e.target.checked})}
                     className="ml-2"
                   />
                   تضمين الملخص المالي
                 </label>
                 <label className="flex items-center">
                   <input
                     type="checkbox"
                     checked={exportOptions.includeFilters}
                     onChange={(e) => setExportOptions({...exportOptions, includeFilters: e.target.checked})}
                     className="ml-2"
                   />
                   تضمين معلومات الفلاتر
                 </label>
               </div>
             </div>
           </div>
           
      
<div className="mt-6 p-4 bg-gray-50 rounded-lg">
  <h4 className="font-medium text-gray-700 mb-2">معاينة التصدير:</h4>
  <div className="text-sm text-gray-600">
    <p>عدد المعاملات: {
      exportOptions.dateRange === 'current' ? paginatedTransactions.length :
      exportOptions.dateRange === 'filtered' ? virtualPaginationInfo.totalCount :
      exportOptions.dateRange === 'all' ? 'سيتم تحميلها عند التصدير' :
      'سيتم تحديدها عند التصدير'
    }</p>
    <p>النوع: {exportOptions.format === 'excel' ? 'Excel متقدم' : exportOptions.format === 'csv' ? 'CSV بسيط' : 'PDF'}</p>
    <p>الفترة: {
      exportOptions.dateRange === 'current' ? 'البيانات المعروضة حاليًا' :
      exportOptions.dateRange === 'filtered' ? 'البيانات المفلترة' :
      exportOptions.dateRange === 'all' ? 'جميع البيانات' :
      'فترة مخصصة'
    }</p>
  </div>
</div>
           
           <div className="flex justify-end gap-3 mt-6 max-md:sticky max-md:bottom-0 max-md:bg-white max-md:py-3 max-md:flex-col-reverse">
             <button
               onClick={() => setIsExportModalOpen(false)}
               className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3 max-md:rounded-xl max-md:text-base"
             >
               إلغاء
             </button>
             <button
               onClick={executeExport}
               disabled={exportOptions.selectedTypes.length === 0}
               className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 max-md:w-full max-md:justify-center max-md:py-3 max-md:rounded-xl max-md:text-base"
             >
               <Download size={18} />
               تصدير الآن
             </button>
           </div>
         </div>
       </div>
     )}
     
     {/* نافذة إضافة/تعديل معاملة */}
     {isTransactionModalOpen && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-md:items-end">
         <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl p-6 m-4 max-h-[90vh] overflow-y-auto max-md:m-0 max-md:max-w-none max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:rounded-none max-md:p-4">
           <h2 className="text-2xl font-bold mb-6 flex items-center max-md:text-xl max-md:sticky max-md:top-0 max-md:bg-white max-md:z-10 max-md:py-3 max-md:mb-4">
             <DollarSign className="text-blue-600 ml-2" />
             {editingTransaction ? 'تعديل معاملة' : 'إضافة معاملة جديدة'}
           </h2>
           
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-md:gap-3">
  {/* نوع المعاملة */}
  <div className="col-span-2 max-md:col-span-1">
    <label className="block text-sm font-medium text-gray-700 mb-2">نوع المعاملة</label>
    <div className="flex gap-4 max-md:grid max-md:grid-cols-2 max-md:gap-3">
      <label className="flex items-center max-md:bg-green-50 max-md:border max-md:border-green-100 max-md:rounded-xl max-md:px-3 max-md:py-3 max-md:justify-center">
        <input
          type="radio"
          value="income"
          checked={transactionForm.type === 'income'}
          onChange={(e) => setTransactionForm({...transactionForm, type: e.target.value})}
          className="ml-2"
        />
        <span className="flex items-center text-green-600">
          <TrendingUp size={18} className="ml-1" />
          إيراد
        </span>
      </label>
      <label className="flex items-center max-md:bg-red-50 max-md:border max-md:border-red-100 max-md:rounded-xl max-md:px-3 max-md:py-3 max-md:justify-center">
        <input
          type="radio"
          value="expense"
          checked={transactionForm.type === 'expense'}
          onChange={(e) => setTransactionForm({...transactionForm, type: e.target.value})}
          className="ml-2"
        />
        <span className="flex items-center text-red-600">
          <TrendingDown size={18} className="ml-1" />
          مصروف
        </span>
      </label>
    </div>
  </div>
  
  {/* المبلغ */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">المبلغ *</label>
    <input
      type="number"
      value={transactionForm.amount}
      onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      placeholder="0.00"
      min="0"
      step="0.01"
    />
  </div>
  
{/* التصنيف مع البحث */}
<div className="relative category-dropdown max-md:z-20">
  <label className="block text-sm font-medium text-gray-700 mb-2">التصنيف *</label>
  <input
    type="text"
    value={categorySearch}
    onChange={(e) => {
      setCategorySearch(e.target.value);
      setShowCategoryDropdown(true);
      if (e.target.value === '') {
        setTransactionForm({...transactionForm, categoryId: ''});
      }
    }}
    onFocus={() => setShowCategoryDropdown(true)}
    className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
    placeholder="ابحث عن التصنيف أو اكتب اسمه..."
  />
  
  {/* زر مسح البحث */}
  {categorySearch && (
    <button
      type="button"
      onClick={clearCategorySearch}
      className="absolute left-3 top-9 text-gray-400 hover:text-gray-600"
    >
      <X size={16} />
    </button>
  )}
  
  {/* أيقونة البحث */}
  <Search className="absolute left-3 top-9 h-4 w-4 text-gray-400 pointer-events-none" />
  
  {/* قائمة التصنيفات المنسدلة */}
  {showCategoryDropdown && (
    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto max-md:fixed max-md:left-3 max-md:right-3 max-md:bottom-3 max-md:w-auto max-md:max-h-[45dvh] max-md:rounded-2xl max-md:z-50">
      {getFilteredCategories().length > 0 ? (
        <>
          {getFilteredCategories().map(category => (
            <button
              key={category.id}
              type="button"
              onClick={() => selectCategory(category)}
              className={`w-full px-4 py-3 text-right hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-b-0 ${
                transactionForm.categoryId === category.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
              }`}
            >
              <span className="font-medium">{category.name}</span>
              {category.description && (
                <span className="text-xs text-gray-500 mr-2">{category.description}</span>
              )}
            </button>
          ))}
          
          {/* خيار إضافة تصنيف جديد */}
          {categorySearch && !getFilteredCategories().find(cat => cat.name.toLowerCase() === categorySearch.toLowerCase()) && (
            <div className="border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setIsCategoryModalOpen(true);
                  setShowCategoryDropdown(false);
                  setCategoryForm({
                    name: categorySearch,
                    type: transactionForm.type,
                    description: ''
                  });
                }}
                className="w-full px-4 py-3 text-right text-blue-600 hover:bg-blue-50 flex items-center"
              >
                <Plus size={16} className="ml-2" />
                إضافة تصنيف جديد "{categorySearch}"
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="px-4 py-3 text-center text-gray-500">
          <p>لا توجد تصنيفات تطابق البحث</p>
          {categorySearch && (
            <button
              type="button"
              onClick={() => {
                setIsCategoryModalOpen(true);
                setShowCategoryDropdown(false);
                setCategoryForm({
                  name: categorySearch,
                  type: transactionForm.type,
                  description: ''
                });
              }}
              className="mt-2 text-blue-600 hover:text-blue-800 flex items-center mx-auto"
            >
              <Plus size={16} className="ml-1" />
              إضافة "{categorySearch}" كتصنيف جديد
            </button>
          )}
        </div>
      )}
    </div>
  )}
  
  {/* عرض التصنيف المختار */}
  {transactionForm.categoryId && (
    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
      <span className="text-sm text-green-700 flex items-center">
        <Tag size={14} className="ml-1" />
        التصنيف المختار: {getCategoryName(transactionForm.categoryId)}
      </span>
    </div>
  )}
</div>
  
  {/* طريقة الدفع */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">الحساب البنكي *</label>
    <select
      value={transactionForm.paymentMethodId}
      onChange={handlePaymentMethodChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
    >
      <option value="">اختر الحساب البنكي</option>
      {paymentMethods
        .filter(method => method.type === 'bank')
        .map(method => (
          <option key={method.id} value={method.id}>
            {method.Name} - {method.categoryName || 'بدون تصنيف'}
          </option>
        ))
      }
    </select>
  </div>
  
  {/* التاريخ */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">التاريخ *</label>
    <input
      type="date"
      value={transactionForm.date}
      onChange={(e) => setTransactionForm({...transactionForm, date: e.target.value})}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
    />
  </div>
  
  {/* الوصف */}
  <div className="col-span-2 max-md:col-span-1">
    <label className="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
    <input
      type="text"
      value={transactionForm.description || ''}
      onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      placeholder="وصف المعاملة"
    />
  </div>
  
  {/* الملاحظات */}
  <div className="col-span-2 max-md:col-span-1">
    <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظات</label>
    <textarea
      value={transactionForm.notes}
      onChange={(e) => setTransactionForm({...transactionForm, notes: e.target.value})}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      rows="3"
      placeholder="ملاحظات إضافية"
    />
  </div>
</div>
           
           <div className="flex justify-end gap-3 mt-6 max-md:sticky max-md:bottom-0 max-md:bg-white max-md:py-3 max-md:flex-col-reverse">
             <button
               onClick={resetTransactionForm}
               className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3 max-md:rounded-xl max-md:text-base"
             >
               إلغاء
             </button>
             <button
               onClick={editingTransaction ? handleEditTransaction : handleAddTransaction}
               disabled={loading}
               className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 max-md:w-full max-md:justify-center max-md:py-3 max-md:rounded-xl max-md:text-base"
             >
               {loading ? (
                 <>
                   <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                   جاري الحفظ...
                 </>
               ) : (
                 <>
                   <Save size={18} />
                   {editingTransaction ? 'تحديث' : 'حفظ'}
                 </>
               )}
             </button>
           </div>
         </div>
       </div>
     )}
     
     {/* نافذة إدارة التصنيفات */}
     {isCategoryModalOpen && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-md:items-end">
         <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl p-6 m-4 max-h-[90vh] overflow-y-auto max-md:m-0 max-md:max-w-none max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:rounded-none max-md:p-4">
           <h2 className="text-2xl font-bold mb-6 flex items-center max-md:text-xl max-md:sticky max-md:top-0 max-md:bg-white max-md:z-10 max-md:py-3 max-md:mb-4">
             <Tag className="text-blue-600 ml-2" />
             إدارة التصنيفات
           </h2>
           
           {/* نموذج إضافة/تعديل تصنيف */}
           <div className="bg-gray-50 p-4 rounded-lg mb-6 max-md:rounded-2xl max-md:p-3">
             <h3 className="text-lg font-medium mb-4">
               {editingCategory ? 'تعديل تصنيف' : 'إضافة تصنيف جديد'}
             </h3>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-md:gap-3">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">اسم التصنيف *</label>
                 <input
                   type="text"
                   value={categoryForm.name}
                   onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                   placeholder="مثال: كهرباء، تذاكر..."
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">النوع *</label>
                 <select
                   value={categoryForm.type}
                   onChange={(e) => setCategoryForm({...categoryForm, type: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                 >
                   <option value="income">إيراد</option>
                   <option value="expense">مصروف</option>
                 </select>
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
                 <input
                   type="text"
                   value={categoryForm.description}
                   onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                   placeholder="وصف اختياري"
                 />
               </div>
             </div>
             <div className="flex justify-end gap-2 mt-4 max-md:grid max-md:grid-cols-2">
               <button
                 onClick={() => {
                   setCategoryForm({ name: '', type: 'expense', description: '' });
                   setEditingCategory(null);
                 }}
                 className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3 max-md:rounded-xl max-md:text-base"
               >
                 إلغاء
               </button>
               <button
                 onClick={editingCategory ? handleEditCategory : handleAddCategory}
                 disabled={loading}
                 className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 max-md:w-full max-md:justify-center max-md:py-3 max-md:rounded-xl max-md:text-base"
               >
                 {loading ? (
                   <>
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                     جاري الحفظ...
                   </>
                 ) : (
                   <>
                     <Save size={18} />
                     {editingCategory ? 'تحديث' : 'حفظ'}
                   </>
                 )}
               </button>
             </div>
           </div>
           
           {/* قائمة التصنيفات */}
           <div className="overflow-x-auto max-md:hidden">
             <table className="min-w-full divide-y divide-gray-200">
               <thead className="bg-gray-50">
                 <tr>
                   <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                     اسم التصنيف
                   </th>
                   <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                     النوع
                   </th>
                   <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                     الوصف
                   </th>
                   <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                     الإجراءات
                   </th>
                 </tr>
               </thead>
               <tbody className="bg-white divide-y divide-gray-200">
                 {categories.map((category) => (
                   <tr key={category.id}>
                     <td className="px-6 py-4 whitespace-nowrap">
                       <span className="text-sm font-medium text-gray-900">{category.name}</span>
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap">
                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                         category.type === 'income' 
                           ? 'bg-green-100 text-green-800' 
                           : 'bg-red-100 text-red-800'
                       }`}>
                         {category.type === 'income' ? 'إيراد' : 'مصروف'}
                       </span>
                     </td>
                     <td className="px-6 py-4">
                       <span className="text-sm text-gray-500">{category.description || '-'}</span>
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap text-center">
                       <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse">
                         {canDo('إدارة التصنيفات') && (
                         <button
                           onClick={() => startEditCategory(category)}
                           className="text-yellow-600 hover:text-yellow-900 p-1"
                           title="تعديل"
                         >
                           <Edit size={18} />
                         </button>
                         )}
                         {canDo('إدارة التصنيفات') && (
                         <button
                           onClick={() => handleDeleteCategory(category.id)}
                           className="text-red-600 hover:text-red-900 p-1"
                           title="حذف"
                         >
                           <Trash2 size={18} />
                         </button>
                         )}
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>

           {/* كروت التصنيفات للموبايل فقط */}
           <div className="md:hidden space-y-3">
             {categories.map((category) => (
               <div key={`mobile-category-${category.id}`} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                 <div className="flex items-start justify-between gap-3">
                   <div className="min-w-0">
                     <div className="font-bold text-gray-900 break-words">{category.name}</div>
                     <div className="text-xs text-gray-500 mt-1 break-words">{category.description || 'بدون وصف'}</div>
                   </div>
                   <span className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                     category.type === 'income' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                   }`}>
                     {category.type === 'income' ? 'إيراد' : 'مصروف'}
                   </span>
                 </div>
                 {canDo('إدارة التصنيفات') && (
                   <div className="grid grid-cols-2 gap-2 mt-3">
                     <button
                       onClick={() => startEditCategory(category)}
                       className="bg-yellow-50 text-yellow-700 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1"
                     >
                       <Edit size={16} />
                       تعديل
                     </button>
                     <button
                       onClick={() => handleDeleteCategory(category.id)}
                       className="bg-red-50 text-red-700 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1"
                     >
                       <Trash2 size={16} />
                       حذف
                     </button>
                   </div>
                 )}
               </div>
             ))}
           </div>
           
           <div className="flex justify-end mt-6 max-md:sticky max-md:bottom-0 max-md:bg-white max-md:py-3">
             <button
               onClick={resetCategoryForm}
               className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3 max-md:rounded-xl max-md:text-base"
             >
               إغلاق
             </button>
           </div>
         </div>
       </div>
     )}
     
     {/* نافذة عرض تفاصيل المعاملة */}
     {viewingTransaction && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-md:items-end">
         <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 m-4 max-md:m-0 max-md:max-w-none max-md:h-[100dvh] max-md:rounded-none max-md:p-4 max-md:overflow-y-auto">
           <h2 className="text-2xl font-bold mb-6 flex items-center max-md:text-xl max-md:sticky max-md:top-0 max-md:bg-white max-md:z-10 max-md:py-3 max-md:mb-4">
             <Eye className="text-blue-600 ml-2" />
             تفاصيل المعاملة
           </h2>
           
           <div className="space-y-4">
             <div className="flex justify-between items-center py-3 border-b">
               <span className="text-gray-600">التاريخ:</span>
               <span className="font-medium">{formatDate(viewingTransaction.date)}</span>
             </div>
             
             <div className="flex justify-between items-center py-3 border-b">
               <span className="text-gray-600">النوع:</span>
               <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                 viewingTransaction.type === 'income' 
                   ? 'bg-green-100 text-green-800' 
                   : 'bg-red-100 text-red-800'
               }`}>
                 {viewingTransaction.type === 'income' ? 'إيراد' : 'مصروف'}
               </span>
             </div>
             
             <div className="flex justify-between items-center py-3 border-b">
               <span className="text-gray-600">المبلغ:</span>
               <span className={`font-bold text-lg ${
                 viewingTransaction.type === 'income' ? 'text-green-600' : 'text-red-600'
               }`}>
                 {formatAmount(viewingTransaction.amount)}
               </span>
             </div>
             
             <div className="flex justify-between items-center py-3 border-b">
               <span className="text-gray-600">التصنيف:</span>
               <span className="font-medium">{getCategoryName(viewingTransaction.categoryId)}</span>
             </div>
             
             <div className="flex justify-between items-center py-3 border-b">
               <span className="text-gray-600">طريقة الدفع:</span>
               <span className="font-medium">{getPaymentMethodName(viewingTransaction.paymentMethodId)}</span>
             </div>
             
             {viewingTransaction.description && (
               <div className="py-3 border-b">
                 <span className="text-gray-600 block mb-2">الوصف:</span>
                 <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{viewingTransaction.description}</p>
               </div>
             )}
             
             {viewingTransaction.notes && (
               <div className="py-3">
                 <span className="text-gray-600 block mb-2">الملاحظات:</span>
                 <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{viewingTransaction.notes}</p>
               </div>
             )}
             
             <div className="text-xs text-gray-500 pt-4">
               <div className="flex items-center gap-1">
                 <Clock size={14} />
                 <span>تم الإنشاء: {new Date(viewingTransaction.createdAt).toLocaleString('ar-EG')}</span>
               </div>
               {viewingTransaction.updatedAt && (
                 <div className="flex items-center gap-1 mt-1">
                   <Clock size={14} />
                   <span>آخر تحديث: {new Date(viewingTransaction.updatedAt).toLocaleString('ar-EG')}</span>
                 </div>
               )}
             </div>
           </div>
           
           <div className="flex justify-end mt-6 max-md:sticky max-md:bottom-0 max-md:bg-white max-md:py-3">
             <button
               onClick={() => setViewingTransaction(null)}
               className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3 max-md:rounded-xl max-md:text-base"
             >
               إغلاق
             </button>
           </div>
         </div>
       </div>
     )}


{/* تنقل الصفحات المحسن */}
{virtualPaginationInfo.totalPages > 1 && (
  <div className="bg-gray-50 px-6 py-4 text-center border-t max-md:px-3 max-md:rounded-2xl max-md:mt-3">
    <div className="flex justify-between items-center mb-3 max-md:grid max-md:grid-cols-2 max-md:gap-2">
      <button
        onClick={() => setVirtualPagination(prev => ({
          ...prev, 
          currentPage: Math.max(1, prev.currentPage - 1)
        }))}
        disabled={virtualPagination.currentPage === 1}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 max-md:justify-center max-md:rounded-xl"
      >
        <ChevronUp className="rotate-90" size={16} />
        الصفحة السابقة
      </button>
      
      <div className="text-center">
        <span className="text-gray-600 font-medium">
          الصفحة {virtualPagination.currentPage} من {virtualPaginationInfo.totalPages}
        </span>
        <div className="text-xs text-gray-500 mt-1">
          عرض {virtualPagination.itemsPerPage} معاملة في الصفحة | 
          المجموع: {virtualPaginationInfo.totalCount} معاملة
        </div>
      </div>
      
      <button
        onClick={() => setVirtualPagination(prev => ({
          ...prev, 
          currentPage: Math.min(virtualPaginationInfo.totalPages, prev.currentPage + 1)
        }))}
        disabled={virtualPagination.currentPage === virtualPaginationInfo.totalPages}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 max-md:justify-center max-md:rounded-xl"
      >
        الصفحة التالية
        <ChevronDown className="rotate-90" size={16} />
      </button>
    </div>
    
    {/* شريط التقدم البصري */}
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
        style={{
          width: `${(virtualPagination.currentPage / virtualPaginationInfo.totalPages) * 100}%`
        }}
      ></div>
    </div>
    
    {/* روابط سريعة للصفحات */}
    {virtualPaginationInfo.totalPages <= 10 && (
      <div className="flex justify-center gap-1 mt-3">
        {Array.from({length: virtualPaginationInfo.totalPages}, (_, i) => i + 1).map(pageNum => (
          <button
            key={pageNum}
            onClick={() => setVirtualPagination(prev => ({...prev, currentPage: pageNum}))}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              pageNum === virtualPagination.currentPage
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {pageNum}
          </button>
        ))}
      </div>
    )}
  </div>
)}

   </div>
 );
};

export default CompanyTransactions;